import axios, {AxiosRequestConfig, Method} from 'axios';
import clsx from 'clsx';
import {Formik, FormikProps} from 'formik';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Button, Spinner, Stack} from 'react-bootstrap';
import {toast} from 'react-toastify';
import * as Yup from 'yup';
import {AppContext} from '../../../../appContext/AppContext';
import FilePreview from '../../../../component/button-in-phieu/components/FilePreview';
import FormInPhieuDialog from '../../../../component/button-in-phieu/components/PrintDialog';
import ModalEditOneColumn from '../../modal-ke-thuoc/ModalEditOneColumn';
import {
  ACTION_CODE,
  APP_CONFIGURATION_KEYS,
  CODE,
  COMPONENT_TYPE,
  ITEM_DATA_SOURCE,
  RESPONSE_MESSAGE
} from '../../../../utils/Constant';
import {formatDateTime, getErrorMessage, parseJSON, replaceTemplateValues,} from '../../../../utils/FormatUtils';
import {useBenhAnContext} from '../../../contexts/BenhAnContext';
import DanhSachPhieu from './DanhSachPhieu';
import PhieuLayout from './PhieuLayout';
import {getJsonGenerateFormNoiTru,} from '../../../services/BenhAnServices';
import {OctGenerateForm} from '@oceantech/oceantech-ui';
import {
  convertDataByConfig,
  convertFlatToObject,
  generateInitValues,
  replacePathVariablesTemplate
} from '../../../../utils/AppFunction';
import {usePhanHeKhamBenhContext} from "../../../contexts/PhanHeTiepDonContext";
import {localStorageItem} from "../../../../utils/LocalStorage";
import {KEY_LOCALSTORAGE} from "../../../../auth/core/_consts";
import CustomTabMenu from "../../../../component/CustomTabMenu";
import {hasAuthority} from "../../../../utils/Permission";
import {convertPayloadSubmit, convertRecordData} from "../../../helpers/modalBenhAn";
import {CONFIG_CODES} from "../../../constants/BenhAn";

const PhieuTemplate = () => {
  const {benhNhanInfo} = usePhanHeKhamBenhContext();
  const {phieuBenhAn, currentUser} = useBenhAnContext();
  const {setIsLoading} = useContext(AppContext);

  const [records, setRecords] = useState<any[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPrintModal, setOpenPrintModal] = useState<boolean>(false);
  const [formJSON, setFormJSON] = useState();
  const [pdfForm, setPdfForm] = useState<any>();
  const [tabValue, setTabValue] = useState<string | null>(null);
  const [tabLoading, setTabLoading] = useState<boolean>(false);
  const [permission, setPermission] = useState({
    view: true,
    edit: true,
    print: true,
  });

  const formikRef = useRef<FormikProps<any>>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const cacheBenhAnPhieu = useRef<any>(null);

  const attributes = phieuBenhAn.attributes;
  const isMultipleRecord = Boolean(Number(attributes.isMultiple || 0));
  const isFile = COMPONENT_TYPE.FILE === attributes.renderType
  const isFormByUrl = COMPONENT_TYPE.FORM === attributes.renderType && attributes.itemDataSource === ITEM_DATA_SOURCE.URL;
  const config = parseJSON(attributes?.additionalConfiguration)
  const baseURL = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION)?.[config?.baseUrl || APP_CONFIGURATION_KEYS.API_URL] || process.env.REACT_APP_API_URL || "";
  const accessToken = localStorageItem.get(KEY_LOCALSTORAGE.ACCESS_TOKEN_DECODE) || {};
  const dataBenhNhan = {...benhNhanInfo?.thongTinKhamBenh, termModelType: phieuBenhAn?.code, tabCode: tabValue};
  const hasTab = attributes.renderType === COMPONENT_TYPE.TAB;

  const validationSchema = Yup.object().shape({
    form: Yup.object({}),
  });
  const initialValues = {
    form: {
      ...generateInitValues(formJSON),
      currentUser: {
        name: accessToken.pdn,
        fullName: accessToken.pdn,
        id: accessToken.pid,
      },
    },
  };

  useEffect(() => {
    if (!phieuBenhAn.code) return;
    const isChangePhieu = cacheBenhAnPhieu.current?.code !== phieuBenhAn.code;
    const tabAttributes = getTabAttributes();
    const isTabFile = tabAttributes?.renderType === COMPONENT_TYPE.FILE;

    if (isFile || isTabFile) {
      getPdfForm(config).then(r => r);
      handleChangeTab("");
    } else if (!hasTab) {
      handleGetFormByPhieu().then(r => r);
      handleChangeTab("");
    }  else if (hasTab && tabValue && !isChangePhieu) {
      handleGetFormByTab().then(r => r);
    } else if (hasTab && !tabValue) {
      const firstItem = phieuBenhAn.filter?.[0] || {}
      handleChangeTab(firstItem?.code || "");
    } else {
      setFormJSON(undefined);
      handleChangeTab("");
    }

    cacheBenhAnPhieu.current = phieuBenhAn;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phieuBenhAn.code, tabValue]);

  useEffect(() => {
    if (!isMultipleRecord) return;

    if (selectedRecord?.id) {
      getDetailRecordItem(selectedRecord);
    } else {
      formikRef?.current?.resetForm({values: initialValues});
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord]);

  const handleGetFormByTab = async () => {
    try {
      setTabLoading(true);
      await getFormJSON(tabValue || "", baseURL);
      isMultipleRecord ? await getRecords() : await getDetailRecord();
    } finally {
      setTabLoading(false);
    }
  }

  const handleGetFormByPhieu = async () => {
    await getFormJSON(phieuBenhAn.code || "", baseURL);
    isMultipleRecord ? await getRecords() : await getDetailRecord();
  }

  const getFormJSON = async (formCode: string, baseURL?: string) => {
    try {
      const {data} = await getJsonGenerateFormNoiTru(formCode, baseURL);
      const jsonForm = parseJSON(data?.data);
      initialValues.form = generateInitValues(jsonForm);
      setFormJSON(jsonForm);
    } catch (error) {
      console.error(error);
      setFormJSON(undefined);
    }
  };

  const getDetailRecord = async () => {
    try {
      let configuration = {...config};

      if (hasTab && tabValue) {
        configuration = getTabConfig();
      }

      const response: any = await getRequestFromConfig(configuration.data, dataBenhNhan, {baseURL});

      if (CODE.SUCCESS !== response?.data?.code) {
        toast.error(getErrorMessage(response));
        return;
      }

      const data = {...response?.data?.data, currentUser}
      const additionalData = convertDataByConfig(configuration.fields || [], data);
      const convertedData = convertRecordData(additionalData, initialValues.form);

      formikRef.current?.setValues({form: convertedData});
      setSelectedRecord(convertedData);
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const getRecords = async () => {
    try {
      let configuration = {...config};

      if (hasTab && tabValue) {
        configuration = getTabConfig();
      }

      const {data}: any = await getRequestFromConfig(
        configuration.voucher,
        dataBenhNhan,
        {baseURL},
      );
      if (CODE.SUCCESS === data?.code) {
        const listTemp = data?.data?.content
          .sort(
            (a: any, b: any) =>
              new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
          )
          .map((record: any, index: number) => ({
            ...record,
            index,
            date: formatDateTime(record.createdDate),
          }));

        setRecords(listTemp);
        listTemp?.length ? setSelectedRecord(listTemp[0]) : setSelectedRecord(null);
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const getDetailRecordItem = async (payload: any) => {
    try {
      let configuration = {...config};

      if (hasTab && tabValue) {
        const tabItem = phieuBenhAn.filter?.find((item: any) => item.code === tabValue);
        configuration = parseJSON(tabItem?.attributes?.additionalConfiguration || "") || {};
      }

      const response: any = await getRequestFromConfig(
        configuration.data,
        payload,
        {baseURL},
      );

      if (CODE.SUCCESS !== response?.data?.code) {
        toast.error(getErrorMessage(response));
        return;
      }

      const data = {
        ...response?.data?.data,
        currentUser,
      }
      const additionalData = convertDataByConfig(configuration.fields || [], data);
      const convertedData = convertRecordData(additionalData, initialValues.form);

      formikRef.current?.setValues({form: convertedData});
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const onRecordSelect = (data: any) => {
    setSelectedRecord(data);
    setOpenPrintModal(false);
  };

  const onRecordAdd = () => {
    const listTemp = [
      {
        id: null,
        date: formatDateTime(new Date()),
      },
      ...records,
    ].map((item: any, index: number) => ({...item, index}));
    setRecords(listTemp);
    setSelectedRecord(listTemp[0]);
  };

  const onRecordDelete = () => {
    if (selectedRecord?.id) {
      setOpenDeleteDialog(true);
    } else {
      let newDsBienBan = records.filter((item: any) => item?.id);
      setRecords(newDsBienBan);
      setSelectedRecord(newDsBienBan[0]);
    }
  };

  const getActionConfigByCode = (code: string) => {
    let newConfig = {...config};
    if (hasTab && tabValue) {
      const config = getTabConfig();
      return config?.actions?.find((x: any) => x?.code === code)
    }

    if (newConfig.item) {
      newConfig = {...newConfig, ...newConfig.item};
    }

    return config?.actions?.find((x: any) => x?.code === code)
  }

  const onConfirmDelete = async () => {
    try {
      if (!selectedRecord) return;

      const configDelete = getActionConfigByCode(ACTION_CODE.DELETE);
      await getRequestFromConfig(
        {...configDelete},
        selectedRecord,
        {baseURL},
      );

      await getRecords();
      setOpenDeleteDialog(false);
      toast.success('Xóa phiếu thành công');
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const onSubmit = async (values: any) => {
    try {
      const {form} = values;
      const params = {
        ...form,
        treatmentId: dataBenhNhan?.treatmentId,
        termModelType: phieuBenhAn.code,
        tabCode: tabValue,
      };
      const configUpdate = getActionConfigByCode(ACTION_CODE.UPDATE);
      const configCreate = getActionConfigByCode(ACTION_CODE.CREATE);
      const scriptConfig = getTabConfig()?.[CONFIG_CODES.SCRIPT_FIELDS];
      const isUpdate = Boolean(form.id);
      const payload = convertPayloadSubmit(dataBenhNhan, params, scriptConfig);

      const configData = isUpdate ? configUpdate : configCreate
      const response: any = await getRequestFromConfig(
        {...configData, payloadTemplate: payload},
        payload,
        {baseURL},
      );
      if (CODE.SUCCESS !== response?.data?.code) {
        toast.error(getErrorMessage(response));
        return;
      }

      toast.success(`${isUpdate ? 'Cập nhật' : 'Tạo'} phiếu thành công`);
      if (isMultipleRecord) {
        !isUpdate ? await getRecords() : await getDetailRecordItem(response?.data?.data);
      } else {
        await getDetailRecord();
      }
    } catch (error) {
      console.error(error);
      toast.error(RESPONSE_MESSAGE.ERROR);
    }
  };

  const getRequestFromConfig = (
    config: {
      method: Method;
      url: string;
      payloadTemplate: any;
      requestParameters: any;
      pathVariables?: any;
    },
    payload: any,
    axiosConfig?: AxiosRequestConfig
  ) => {
    const {url, method, payloadTemplate, requestParameters, pathVariables} = config;

    let finalUrl = replacePathVariablesTemplate(url, pathVariables, payload);
    const data = replaceTemplateValues(payloadTemplate, payload);
    const params = replaceTemplateValues(requestParameters, payload);

    return axios({
      url: finalUrl,
      method,
      data,
      params,
      ...axiosConfig,
    });
  };

  const getPdfForm = async (config: any) => {
    try {
      setIsLoading(true);
      const request = getRequestFromConfig(
        config.data,
        dataBenhNhan,
        {
          baseURL,
          responseType: 'blob',
        }
      );

      const {data} = await request;
      const blob = new Blob([data], {type: 'application/pdf'});
      const urlPreview = URL.createObjectURL(blob);
      setPdfForm(urlPreview);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const printRecord = async () => {
    const configPrint = getActionConfigByCode(ACTION_CODE.PRINT);
    const data = {...selectedRecord, termModelType: phieuBenhAn?.code};
    return getRequestFromConfig(
      configPrint,
      data,
      {
        baseURL,
        responseType: 'blob',
      }
    );
  };

  const handleCheckPermission = (tab?: string) => {
    const permission = {
      edit: true,
      view: true,
      print: true
    }

    if (hasTab && tabValue && tab) {
      const tabItem = phieuBenhAn.filter?.find((item: any) => item.code === tab);
      const editPermission = tabItem?.attributes.editAuthority || "";
      const viewPermission = tabItem?.attributes.viewAuthority || "";

      permission.edit = !editPermission || hasAuthority(editPermission);
      permission.view = !viewPermission || hasAuthority(viewPermission);
    }

    setPermission(permission);
  }

  const handleChangeTab = (tab: string, callback?: () => void) => {
    if (tab !== tabValue) {
      callback?.();
    }
    handleCheckPermission(tab);
    setTabValue(tab);
  }

  const getTabConfig = (tab?: string) => {
    const tabAttributes = getTabAttributes(tab);
    let config = parseJSON(tabAttributes?.additionalConfiguration || "") || {};

    if (config.item) {
      config = {...config, ...config.item};
    }

    return config;
  }

  const getTabAttributes = (tab?: string) => {
    const tabItem = phieuBenhAn.filter?.find((item: any) => item.code === tab || tabValue);
    return tabItem?.attributes || {};
  }

  return (
    <PhieuLayout>
      {isMultipleRecord && (
        <PhieuLayout.Left>
          <DanhSachPhieu
            data={records}
            onItemSelect={onRecordSelect}
            selectedRow={selectedRecord}
          />
        </PhieuLayout.Left>
      )}

      <PhieuLayout.Main>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onSubmit(values);
          }}
          validationSchema={validationSchema}
          innerRef={formikRef}
        >
          {({values, setValues, setFieldValue, submitForm, errors, touched, resetForm}) => {
            const formComponent = () => {

              if (isFile) {
                return (
                  <FilePreview
                    src={pdfForm}
                    title={phieuBenhAn.code}
                    className="h-100"
                    ref={iframeRef}
                  />
                )
              }

              if (hasTab) {
                const dsTab = phieuBenhAn.filter?.map((item: any) => ({
                  eventKey: item?.code,
                  title: item?.name,
                  component: (<div></div>)
                }))
                return (
                  <>
                    <CustomTabMenu
                      danhsachTabs={dsTab}
                      activeTab={tabValue}
                      onTabChange={(activeTab: any) => {
                        handleChangeTab(activeTab, resetForm);
                      }}
                    />
                    <OctGenerateForm
                      isView={isMultipleRecord && !selectedRecord}
                      isUpdate={!tabValue}
                      listFieldAuto={formJSON}
                      values={values}
                      errors={errors}
                      touched={touched}
                      setValues={setValues}
                      setFieldValue={setFieldValue}
                      modelID="form"
                    />
                  </>
                )
              }

              return (
                <OctGenerateForm
                  isView={isMultipleRecord && !selectedRecord}
                  listFieldAuto={formJSON}
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  modelID="form"
                />
              )
            }
            return (
              <PhieuLayout.Content>
                <PhieuLayout.Form
                  loading={true}
                  className={clsx({
                    'h-100': isFile || hasTab,
                  })}
                >
                  {formComponent()}
                </PhieuLayout.Form>
                <PhieuLayout.Action>
                  {(isFile) ? (
                    <Stack
                      className="w-100 gap-2 justify-content-end"
                      direction="horizontal"
                    >
                      <Button
                        className="btn-fill spaces w-60px"
                        onClick={() => {
                          if (iframeRef.current) {
                            iframeRef.current.focus();
                            iframeRef.current.contentWindow!.print();
                          }
                        }}
                      >
                        <i className="bi bi-printer"></i>
                        In
                      </Button>
                    </Stack>
                  ) : (
                    <Stack
                      className="w-100 gap-2 justify-content-end"
                      direction="horizontal"
                    >
                      {isMultipleRecord && (
                        <>
                          <Button
                            className="btn-fill"
                            onClick={onRecordAdd}
                            disabled={records?.some(
                              (item: any) => !item?.id
                            )}
                          >
                            <i className="bi bi-plus"></i>
                            Thêm
                          </Button>
                          <Button
                            className="btn-fill btn-danger"
                            onClick={onRecordDelete}
                            disabled={!selectedRecord}
                          >
                            <i className="bi bi-x"></i>
                            Xóa
                          </Button>
                        </>
                      )}
                      <Button
                        className="btn-fill"
                        onClick={() => {
                          submitForm();
                        }}
                        disabled={isMultipleRecord && !selectedRecord || !permission.edit}
                      >
                        <i className="bi bi-save"></i>
                        Lưu
                      </Button>
                      <Button
                        className="btn-fill btn-danger"
                        onClick={() => {
                          setOpenPrintModal(true);
                        }}
                        disabled={isMultipleRecord || !selectedRecord?.id}
                      >
                        <i className="bi bi-printer"></i>
                        In
                      </Button>
                    </Stack>
                  )}
                </PhieuLayout.Action>
              </PhieuLayout.Content>
            )
          }}
        </Formik>
      </PhieuLayout.Main>

      {openDeleteDialog && (
        <ModalEditOneColumn
          name="Lý do xóa"
          code="deletedReason"
          handleClose={() => setOpenDeleteDialog(false)}
          handleSave={(reason: string) => onConfirmDelete()}
        />
      )}

      {openPrintModal && selectedRecord?.id && (
        <FormInPhieuDialog<any>
          show={openPrintModal}
          onHide={() => {
            setOpenPrintModal(false);
          }}
          fetchExport={printRecord}
          params={{
            id: selectedRecord?.id,
            termId: phieuBenhAn.termId,
          }}
        />
      )}
    </PhieuLayout>
  );
};

export default PhieuTemplate;
